import { AVAILABLE_LANGUAGES } from '@mission.io/mission-toolkit/constants'

import { useEffect, useState } from 'react'

export const LANGUAGE_TO_TRANSLATION_LANGUAGE: {
	[missionIoLanguage: $Keys<typeof AVAILABLE_LANGUAGES>]: string,
} = {
	[AVAILABLE_LANGUAGES.ENGLISH]: 'en',
	[AVAILABLE_LANGUAGES.SPANISH]: 'es',
	[AVAILABLE_LANGUAGES.PORTUGUESE]: 'pt',
	[AVAILABLE_LANGUAGES.FRENCH]: 'fr',
	[AVAILABLE_LANGUAGES.ARMENIAN]: 'hy',
	[AVAILABLE_LANGUAGES.RUSSIAN]: 'ru',
	[AVAILABLE_LANGUAGES.TAGALOG]: 'tl',
	[AVAILABLE_LANGUAGES.GERMAN]: 'de',
	[AVAILABLE_LANGUAGES.UKRAINIAN]: 'uk',
	[AVAILABLE_LANGUAGES.ARABIC]: 'ar',
	[AVAILABLE_LANGUAGES.CANTONESE]: 'yue',
	[AVAILABLE_LANGUAGES.JAPANESE]: 'ja',
	[AVAILABLE_LANGUAGES.KOREAN]: 'ko',
	[AVAILABLE_LANGUAGES.VIETNAMESE]: 'vi',
}

export type GlobalTranslator = {
	sourceLanguage: string,
	targetLanguage: string,
	monitor: any,
}

export type Translator = {
	inputQuota: number,
	sourceLanguage: string,
	targetLanguage: string,
	destroy: () => void,
	translate: (text: string) => Promise<string>,
}

const TRANSLATOR_STATUSES = {
	LOADING: 'LOADING',
	AVAILABLE: 'AVAILABLE',
	BROKEN: 'BROKEN',
}

type AvailableTranslatorStatus = {|
	type: 'AVAILABLE',
	translator: Translator,
	alreadyTranslated: Map<string, string>,
|}
type TranslatorStatuses =
	| {|
			type: 'LOADING',
			promise: Promise<Translator>,
	  |}
	| AvailableTranslatorStatus
	| {|
			type: 'BROKEN',
	  |}

// a mapping of source and target language to the status of the translator in charge of translating from the source language to the target language
const activeTranslators: Map<
	// source language
	string,
	Map<
		// target language
		string,
		TranslatorStatuses
	>
> = new Map()

/**
 * useCanTranslateToLanguage - a hook used to determine if a translation between languages is supported
 *
 * @param {string} sourceLanguage - the language being translated from
 * @param {string} targetLanguage - the language being translated to
 *
 * @return {{
 *   loading: boolean, - true if in the process of determining if the translation is supported
 *   translationSupported: boolean, - true if the translation is supported false otherwise
 * }}
 */
export function useCanTranslateToLanguage(
	sourceLanguage: string,
	targetLanguage: string
): {| loading: true |} | {| loading: false, translationSupported: boolean |} {
	const [status, setStatus] = useState({
		loading: true,
	})

	useEffect(() => {
		let canceled = false

		if (sourceLanguage === targetLanguage) {
			setStatus({ loading: false, translationSupported: true })
			return
		}

		getTranslator(sourceLanguage, targetLanguage)
			.then(() => {
				if (canceled) {
					return
				}
				setStatus({ loading: false, translationSupported: true })
			})
			.catch(err => {
				console.error(err)
				if (canceled) {
					return
				}
				setStatus({ loading: false, translationSupported: false })
			})

		return () => {
			canceled = true
		}
	}, [sourceLanguage, targetLanguage])

	return status
}

/**
 * getTranslator - get a translator between the two languages
 *
 * @param {string} sourceLanguage - the language being translated from
 * @param {string} targetLanguage - the language being translated to
 *
 * @return {Promise<Translator>} - a promise which resolves to a translator after it has finished loading, will reject if unable to create the translator for any reason
 */
async function getTranslator(
	sourceLanguage: string,
	targetLanguage: string
): Promise<AvailableTranslatorStatus> {
	if (!('Translator' in window)) {
		return Promise.reject('Translator API not supported on this device')
	}

	const _translatorsFromLanguage = activeTranslators.get(sourceLanguage)
	const translatorsFromLanguage = _translatorsFromLanguage ?? new Map()

	if (!_translatorsFromLanguage) {
		activeTranslators.set(sourceLanguage, translatorsFromLanguage)
	}

	let translatorToTargetLanguageStatus = translatorsFromLanguage.get(targetLanguage)
	let translator: Translator | null = null
	if (!translatorToTargetLanguageStatus) {
		// download and display
		try {
			const downloadPromise = window.Translator.create({
				sourceLanguage,
				targetLanguage,
				monitor(m) {
					m.addEventListener('downloadprogress', e => {
						console.info(
							`Downloaded translation model: "${sourceLanguage}" -> "${targetLanguage}" ${(e.loaded /
								(e.total ?? 1)) *
								100}%`
						)
					})
				},
			})
			translatorsFromLanguage.set(targetLanguage, {
				type: TRANSLATOR_STATUSES.LOADING,
				promise: downloadPromise,
			})

			translator = await downloadPromise
			const updatedTranslatorStatus = {
				type: TRANSLATOR_STATUSES.AVAILABLE,
				translator,
				alreadyTranslated: new Map(),
			}
			translatorsFromLanguage.set(targetLanguage, updatedTranslatorStatus)
			return updatedTranslatorStatus
		} catch (error) {
			console.warn(`could not get translator from ${sourceLanguage} to ${targetLanguage}`)
			translatorsFromLanguage.set(targetLanguage, {
				type: TRANSLATOR_STATUSES.BROKEN,
			})
			return Promise.reject('Could not download model from window.Translator to translate')
		}
	} else if (translatorToTargetLanguageStatus.type === TRANSLATOR_STATUSES.LOADING) {
		try {
			await translatorToTargetLanguageStatus.promise

			const updatedStatus = activeTranslators.get(sourceLanguage)?.get(targetLanguage)

			if (!updatedStatus || updatedStatus.type !== TRANSLATOR_STATUSES.AVAILABLE) {
				return Promise.reject('Translator was downloaded, but was not set properly.')
			}

			return updatedStatus
		} catch (error) {
			return Promise.reject('Failed to download model')
		}
	} else if (translatorToTargetLanguageStatus.type === TRANSLATOR_STATUSES.BROKEN) {
		return Promise.reject('Could not download model')
	} else if (translatorToTargetLanguageStatus.type === TRANSLATOR_STATUSES.AVAILABLE) {
		return translatorToTargetLanguageStatus
	}

	return Promise.reject(`Unknown translator status ${translatorToTargetLanguageStatus.type}`)
}

/**
 * translateString - translate string from the source language to the target language
 *
 * @param {string} sourceLanguage - the language the string is currently in
 * @param {string} targetLanguage - the language to translate the string into
 * @param {string} text - the string to translate
 *
 * @return {Promise<string>} - a promise which resolves to the translated string, rejects if translation fails
 */
export async function translateString(
	sourceLanguage: string,
	targetLanguage: string,
	text: string
): Promise<string> {
	if (sourceLanguage === targetLanguage) {
		return text
	}

	const activeTranslatorWrapper = await getTranslator(sourceLanguage, targetLanguage)

	const previouslyTranslatedText = activeTranslatorWrapper.alreadyTranslated.get(text)
	if (previouslyTranslatedText != null) {
		return Promise.resolve(previouslyTranslatedText)
	}

	return activeTranslatorWrapper.translator.translate(text).then(translatedText => {
		activeTranslatorWrapper.alreadyTranslated.set(text, translatedText)
		return translatedText
	})
}

export const TEST_ONLY = {
	resetGlobals: () => {
		activeTranslators.clear()
	},
}
